import { useState } from "react";
import { Plus, Upload, ImageIcon } from "lucide-react";
import MainLayout from "../sidebar/Sidebar";
import { useEffect } from "react";
import api from "../api/api.js";
import { useAuth } from "../context/AuthContext.js";

// Simple modal component (Dialog replacement)
function Modal({ open, onClose, children }) {
  if (!open) return null;
  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-md shadow-lg max-w-md w-full p-6"
        onClick={(e) => e.stopPropagation()}
      >
        {children}
      </div>
    </div>
  );
}

export default function PicturesPage() {
  const { user } = useAuth();
  const [location, setLocation] = useState("Pictures");
  const [images, setImages] = useState([]);
  const [filterProject, setFilterProject] = useState("all");
  const [isUploadDialogOpen, setIsUploadDialogOpen] = useState(false);
  const [newImageFile, setNewImageFile] = useState(null);
  const [newImageProject, setNewImageProject] = useState("");

  const [projects, setProjects] = useState([]);

  useEffect(() => {
    async function fetchProjects() {
      try {
        const response = await api.get("/projects/");
        const sortedProjects = response.data.sort(
          (a, b) => new Date(b.created_at) - new Date(a.created_at)
        );
        setProjects(sortedProjects);
      } catch (error) {
        console.error("Failed to fetch projects:", error);
      }
    }

    if (user) fetchProjects();
  }, [user]);

  const handleImageUpload = () => {
    if (newImageFile && newImageProject) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setImages((prev) => [
          ...prev,
          {
            id: `img-${Date.now()}`,
            src: reader.result,
            alt: newImageFile.name,
            projectId: newImageProject,
          },
        ]);
        setNewImageFile(null);
        setNewImageProject("");
        setIsUploadDialogOpen(false);
      };
      reader.readAsDataURL(newImageFile);
    }
  };

  const filteredImages =
    filterProject === "all"
      ? images
      : images.filter((img) => img.projectId === filterProject);

  return (
    <MainLayout location={location} setLocation={setLocation}>
      <div className="flex flex-col gap-6 min-h-full py-4 px-6">
        <div className="flex items-center justify-end w-full">
          <div className="flex items-center gap-4">
            <select
              className="border border-gray-300 rounded px-3 py-1 focus:outline-none focus:ring-2 focus:ring-blue-500"
              value={filterProject}
              onChange={(e) => setFilterProject(e.target.value)}
            >
              <option value="all">All Projects</option>
              {projects.map((project) => (
                <option key={project.id} value={project.id}>
                  {project.name}
                </option>
              ))}
            </select>

            <button
              onClick={() => setIsUploadDialogOpen(true)}
              className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <Plus className="mr-2 h-4 w-4" />
              Upload Picture
            </button>
          </div>
        </div>

        <Modal
          open={isUploadDialogOpen}
          onClose={() => setIsUploadDialogOpen(false)}
        >
          <h2 className="text-xl font-semibold mb-2">Upload New Picture</h2>
          <p className="mb-4 text-gray-600">
            Add a new image and assign it to a project.
          </p>

          <div className="grid grid-cols-4 items-center gap-4 mb-4">
            <label htmlFor="picture" className="text-right font-medium">
              Picture
            </label>
            <input
              id="picture"
              type="file"
              className="col-span-3 border border-gray-300 rounded px-2 py-1"
              onChange={(e) =>
                setNewImageFile(e.target.files ? e.target.files[0] : null)
              }
            />
          </div>

          <div className="grid grid-cols-4 items-center gap-4 mb-6">
            <label htmlFor="project" className="text-right font-medium">
              Project
            </label>
            <select
              id="project"
              className="col-span-3 border border-gray-300 rounded px-3 py-1 focus:outline-none focus:ring-2 focus:ring-blue-500"
              value={newImageProject}
              onChange={(e) => setNewImageProject(e.target.value)}
            >
              <option value="">Select Project</option>
              {projects.map((project) => (
                <option key={project.id} value={project.id}>
                  {project.name}
                </option>
              ))}
            </select>
          </div>

          <div className="flex justify-end gap-2">
            <button
              onClick={() => setIsUploadDialogOpen(false)}
              className="px-4 py-2 rounded border border-gray-300 hover:bg-gray-100"
            >
              Cancel
            </button>
            <button
              onClick={handleImageUpload}
              disabled={!newImageFile || !newImageProject}
              className={`px-4 py-2 rounded text-white ${
                newImageFile && newImageProject
                  ? "bg-blue-600 hover:bg-blue-700"
                  : "bg-blue-300 cursor-not-allowed"
              }`}
            >
              <Upload className="inline-block mr-2 h-4 w-4 align-middle" />
              Upload
            </button>
          </div>
        </Modal>

        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {filteredImages.length === 0 ? (
            <p className="col-span-full text-center text-gray-500">
              No pictures found for this project.
            </p>
          ) : (
            filteredImages.map((image) => (
              <div
                key={image.id}
                className="overflow-hidden rounded-lg shadow-md bg-white"
              >
                <div className="relative w-full h-48 bg-gray-100 flex items-center justify-center rounded-t-lg overflow-hidden">
                  {image.src.startsWith("data:") ||
                  image.src.startsWith("/placeholder.svg") ? (
                    <img
                      src={image.src || "/placeholder.svg"}
                      alt={image.alt}
                      layout="fill"
                      objectFit="cover"
                      className="rounded-t-lg"
                    />
                  ) : (
                    <ImageIcon className="h-12 w-12 text-gray-400" />
                  )}
                </div>
                <div className="px-3 py-2 text-sm text-gray-600 flex justify-between">
                  <p className="truncate">{image.alt}</p>
                  <p className="ml-2 text-xs whitespace-nowrap">
                    {projects.find((p) => p.id === image.projectId)?.name ||
                      "Unassigned"}
                  </p>
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </MainLayout>
  );
}
